import { Component, OnDestroy, ViewChild, OnInit } from '@angular/core';
import { TransferHttpService, CacheItem } from '../../../../services/transfer-http/transfer-http.service';
import { Router, ActivatedRoute } from '@angular/router';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { takeUntil, map } from 'rxjs/operators';
import { Subject, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { QcInspectionReports, QcInspectionReferences, AllSites, WoWorkOrders, QcObservationXjobControlNumbers } from 'src/models';
import { NgForm } from '@angular/forms';
import { AimsCommonConfirmDialogComponent, AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions } from '@aims/common-components';
import { DialogComponent } from 'src/app/services/message/message.enum';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'src/app/services/message/message.service';

@Component({
  selector: 'inspection-report',
  templateUrl: './inspection-report.component.html',
  styleUrls: ['./inspection-report.component.css']
})
export class InspectionReportComponent implements OnInit, OnDestroy {

  // AimsCommonTable setup
  observationsTablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: false,
    delete: false,
  };
  observationsTableHeader: AimsCommonTableHeader[] = [
    { key: 'details', label: 'Details', type: 'button', prefix: '/projectSupport/observation/', linkId: 'observationId', filter: false },
    { key: 'obsInspNum', label: 'Observation(s)', type: 'string', width: 90 },
    { key: 'observationTypeId', label: 'Obs Type', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => term === el.observationTypeId} },
    { key: 'deficiencyTypeId', label: 'Deficiency Type', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => term === el.deficiencyTypeId } },
    { key: 'nonConformance', label: 'Non-Conformance', type: 'dynamic', width: 500 },
    { key: 'areaId', label: 'Area', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => term === el.areaId } },
    { key: 'projMngrReviewedDate', label: 'CAR Closed On', type: 'date', width: 110 },
  ];

  woTablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: false,
    delete: false,
  };
  woTableHeader: AimsCommonTableHeader[] = [
    { key: 'details', label: 'Details', type: 'button', prefix: '/assetMgmt/workOrder/', linkId: 'workOrderId', filter: false },
    { key: 'workOrderNumber', label: 'Work Order(s)', type: 'string', width: 150 },
    { key: 'description', label: 'Description', type: 'string', },
    { key: 'classification.classification', label: 'Classification', type: 'string', width: 150 },
    { key: 'status.status', label: 'Status', type: 'string', width: 150 },
  ];

  jcnTablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: false,
    delete: false,
  };
  jcnTableHeader: AimsCommonTableHeader[] = [
    { key: 'jobControlNumber', label: 'JCN(s)', type: 'string', width: 150 },
    { key: 'observationId', label: 'Linked from Observation(s)', type: 'dynamic', width: 150 },
  ];

  constructor(public transferHttp: TransferHttpService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private messageService: MessageService,
    public permissions: PermissionsService) {
  }
  destroy$: Subject<boolean> = new Subject();

  //***************************
  // Variables
  //***************************

  user = this.transferHttp.getCache(CacheItem.user);

  today = new Date();
  newInspectionReport: BehaviorSubject<boolean> = new BehaviorSubject(false);
  saveClicked: boolean = false;
  showInspectionReport: boolean = false;

  workorders: Array<WoWorkOrders>;
  jcns: Array<QcObservationXjobControlNumbers>;

  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = true;

  employeeLookupColumns:AimsCommonTableHeader[] = [
    { key: 'fullName', label: 'Name', type: AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'jobTitle', label: 'Job Title', type: AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'siteCode', label: 'Permanent Site', type: AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'employmentType', label: 'Employment Type', type: AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'employeeStatus', label: 'Employee Status', type: AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'employmentStatus', label: 'Employment Status', type: AimsCommonTableHeaderType.STRING, filter: true }
  ];

  //Lookup Values
  areaValues = this.transferHttp.getSingle('QualityControl/GetAreas');
  obsTypeValues = this.transferHttp.getSingle('QualityControl/GetObservationTypes');
  defTypeValues = this.transferHttp.getSingle('QualityControl/GetDeficiencyTypes');

  //DropDown Values, if isNew than only get ACTIVE values for the dropdowns using the combinLatest

  employeeValues = combineLatest([
    this.newInspectionReport,
    this.transferHttp.getSingle('Sch/GetAllEmployeesForLookUp')]).pipe(
      map(([isNew, e]) => {
        return e.filter(e => ((isNew) ? e.employmentStatus == "Active" : e.employmentStatus !== null))
      })
  );

  contractNumberValues = combineLatest([
    this.newInspectionReport,
    this.transferHttp.getSingle<Array<QcInspectionReferences>>('QualityControl/GetContractNumbers')]).pipe(
      map(([isNew, c]) => {
        return c.filter(c => ((isNew) ? c.active : c.active !== null))
      }
    )
  );

  siteValues = combineLatest([
    this.newInspectionReport,
    this.transferHttp.getSingle<Array<AllSites>>('Sch/GetAllCompanyOrSanSites')]).pipe(
      map(([isNew, sites]) => {
        return sites.filter(s => ((isNew) ? s.active : s.active !== null))
      }
    )
  );

  inspectionReferenceValues = combineLatest([
    this.newInspectionReport,
    this.transferHttp.getSingle<Array<QcInspectionReferences>>('QualityControl/GetInspectionReferences')]).pipe(
      map(([isNew, refs]) => {
        return refs.filter(r => ((isNew) ? r.active : r.active !== null))
      }
    )
  );

  inspectionReport: QcInspectionReports = new QcInspectionReports();
  @ViewChild('rptForm', { static: false }) rptForm: NgForm;

  //***************************
  // Functions
  //***************************

  rptNotLoaded(): boolean {
    return this.inspectionReport === undefined;
  }

  calculateEditable() {
    let returnVal = false;

    if (this.permissions.canUpdate(this.permissions.ComponentID.InspectionReports)) {
      if (this.inspectionReport.finalReviewDate == null) {
        returnVal = true;
      }
   }
    return returnVal;
  }

  print() {
      this.router.navigate([`/reporting/Inspection_Report_PartA/trdp/{"QtrStart":null,"QtrEnd":null,"IrId":"${this.inspectionReport.inspectionReportId}"}`]);
   }


  //Cancel
  public cancelRptUpdate() {
    var message = this.messageService.getDialogMessage(DialogComponent.UnsavedChanges);
    let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent,{
      data: message
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if(dialogResult){
        this.router.navigate(['/projectSupport']);
      }
      dialogRef = null;
    });

  }

  public qcFinalApproval() {
    this.inspectionReport.finalReviewDate = new Date();
    this.saveInspectionReport();
  }

  //Save Inspection Report
  saveInspectionReport() {
    this.saveClicked = true;
    let saving$: Subscription = this.transferHttp.put(`QualityControl/SaveInspectionReports`, this.inspectionReport).subscribe(ret => {
      this.loadInspectionReport(ret.id);
      saving$.unsubscribe();
    });
  }

  public uploadRemoved() {
    this.loadInspectionReport(this.inspectionReport.inspectionReportId);
    this.showInspectionReport = this.rptNotLoaded();
  }

  loadInspectionReport(inspectionReportId: number) {
    this.router.navigate(['/projectSupport/inspectionReport/'+inspectionReportId]); //added so, you can create another IR back to back
    this.saveClicked = false;
    this.newInspectionReport.next(false);
    this.transferHttp.getSingle<QcInspectionReports>(`QualityControl/GetInspectionReport?inspectionReportId=${inspectionReportId}`).subscribe(retVal => {
      this.inspectionReport = retVal;
      this.workorders = [].concat.apply([], this.inspectionReport.qcObservations.map(o => o.qcObservationXworkOrders.map(x => x.workOrder)));
      this.jcns = [].concat.apply([], this.inspectionReport.qcObservations.map(o => o.qcObservationXjobControlNumbers));

      if (this.rptForm) {
        this.rptForm.form.markAsPristine();
      }
      this.showInspectionReport = this.rptNotLoaded();
    });

  }

  public createInspectionReport() {

    //Set defaults
    this.newInspectionReport.next(true);
    this.inspectionReport.contractNumberId = 1;
    this.inspectionReport.siteId = null;
    this.inspectionReport.reportName = null;
    this.inspectionReport.ref1Id = 1;
    this.inspectionReport.ref2Id = null;
    this.inspectionReport.ref3Id = null;
    this.inspectionReport.ref4Id = null;
    this.inspectionReport.ref5Id = null;
    this.inspectionReport.ref6Id = null;
    this.inspectionReport.ref7Id = null;
    this.inspectionReport.ref8Id = null;
    this.inspectionReport.ref9Id = null;
    this.inspectionReport.sitePersonnel1Id = null;
    this.inspectionReport.sitePersonnel2Id = null;
    this.inspectionReport.sitePersonnel3Id = null;
    this.inspectionReport.sitePersonnel4Id = null;
    this.inspectionReport.isStaChief1 = false;
    this.inspectionReport.isStaChief2 = false;
    this.inspectionReport.isStaChief3 = false;
    this.inspectionReport.isStaChief4 = false;
    this.inspectionReport.inspector1Id = this.user.employeeId;
    this.inspectionReport.inspector2Id = null;
    this.inspectionReport.inspector3Id = null;
    this.inspectionReport.inspector4Id = null;
    this.inspectionReport.inspectionDate = null;
    this.inspectionReport.generalComments = null;
    this.inspectionReport.overallImpressionOfFacilities = null;

    this.siteValues.subscribe();
    this.employeeValues.subscribe();
    this.showInspectionReport = this.rptNotLoaded();
  }

  //***************************
  // Init
  //***************************

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(p => {
      if (p['inspectionReportId'] != null) {
        this.loadInspectionReport(p['inspectionReportId']);
      }
      else {
        this.createInspectionReport();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
